import { useState, useEffect } from 'react';
import { authService, ssoService, rbacService } from '../services';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Shield, Users, UserPlus, Key, Trash2, RefreshCw, 
  Loader2, CheckCircle, XCircle, AlertCircle, Copy, Eye, EyeOff,
  Crown, Calculator, Wrench, Briefcase, Headphones
} from 'lucide-react';
import { toast } from 'sonner';

// Microsoft Logo
const MicrosoftLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

export const SSOConfigPanel = () => {
  const [config, setConfig] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    tenant_type: 'common',
    redirect_uri: `${process.env.REACT_APP_BACKEND_URL}/api/auth/entra/callback`
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const data = await ssoService.getConfig();
      setConfig({
        enabled: data.enabled || false,
        client_id: data.client_id || '',
        client_secret: data.client_secret_set ? '***hidden***' : '',
        tenant_type: data.tenant_type || 'common',
        redirect_uri: data.redirect_uri || `${process.env.REACT_APP_BACKEND_URL}/api/auth/entra/callback`
      });
    } catch (err) {
      console.error('Error loading SSO config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ssoService.updateConfig({
        enabled: config.enabled,
        client_id: config.client_id,
        client_secret: config.client_secret.startsWith('***') ? null : config.client_secret,
        tenant_type: config.tenant_type,
        redirect_uri: config.redirect_uri
      });
      toast.success('SSO-Konfiguration gespeichert');
      loadConfig(); // Reload to get masked secret
    } catch (err) {
      toast.error('Fehler beim Speichern der SSO-Konfiguration');
    } finally {
      setSaving(false);
    }
  };

  const copyRedirectUri = () => {
    navigator.clipboard.writeText(config.redirect_uri);
    toast.success('Redirect URI kopiert');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader>
        <div className="flex items-center gap-3">
          <MicrosoftLogo />
          <div>
            <CardTitle>Microsoft Entra ID (Azure AD)</CardTitle>
            <CardDescription>Single Sign-On Konfiguration</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable SSO */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div>
            <Label className="text-base font-medium">SSO aktivieren</Label>
            <p className="text-sm text-slate-500">
              Ermöglicht die Anmeldung mit Microsoft-Konten
            </p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
        </div>

        {/* Configuration Fields */}
        <div className="space-y-4">
          <div>
            <Label>Client ID (Application ID)</Label>
            <Input
              value={config.client_id}
              onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Client Secret</Label>
            <div className="relative mt-1">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={config.client_secret}
                onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
                placeholder="Client Secret eingeben..."
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Leer lassen, um den vorhandenen Secret zu behalten
            </p>
          </div>

          <div>
            <Label>Tenant-Typ</Label>
            <Select
              value={config.tenant_type}
              onValueChange={(value) => setConfig({ ...config, tenant_type: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="common">Alle Microsoft-Konten (Multi-Tenant + Personal)</SelectItem>
                <SelectItem value="organizations">Nur Geschäfts-/Schulkonten</SelectItem>
                <SelectItem value="consumers">Nur persönliche Microsoft-Konten</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Redirect URI</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={config.redirect_uri}
                onChange={(e) => setConfig({ ...config, redirect_uri: e.target.value })}
                className="flex-1"
              />
              <Button variant="outline" size="icon" onClick={copyRedirectUri}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Diese URI muss in der Azure App-Registrierung als Redirect URI konfiguriert werden
            </p>
          </div>
        </div>

        {/* Setup Instructions */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">Setup-Anleitung</h4>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Gehen Sie zu <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal → App-Registrierungen</a></li>
            <li>Erstellen Sie eine neue Registrierung oder wählen Sie eine vorhandene</li>
            <li>Wählen Sie "Konten in beliebigem Organisationsverzeichnis" für Multi-Tenant</li>
            <li>Fügen Sie die oben angezeigte Redirect URI hinzu</li>
            <li>Erstellen Sie unter "Zertifikate & Geheimnisse" ein neues Client Secret</li>
            <li>Kopieren Sie Application ID und Secret hierher</li>
          </ol>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Konfiguration speichern
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({ global_roles: [], tenant_roles: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    role: 'viewer',
    global_role: ''
  });
  const [createdPassword, setCreatedPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Role icons and colors for display
  const ROLE_CONFIG = {
    // Global roles (Betreiber)
    super_admin: { icon: Crown, color: 'bg-red-100 text-red-800 border-red-200', label: 'Super-Admin' },
    finance: { icon: Calculator, color: 'bg-green-100 text-green-800 border-green-200', label: 'Finanzen' },
    technical_admin: { icon: Wrench, color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Tech-Admin' },
    partner: { icon: Briefcase, color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Partner' },
    // Tenant roles (Mandant)
    tenant_admin: { icon: Shield, color: 'bg-indigo-100 text-indigo-800 border-indigo-200', label: 'Kunden-Admin' },
    accountant: { icon: Calculator, color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Buchhaltung' },
    supporter: { icon: Headphones, color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Support' },
    viewer: { icon: Eye, color: 'bg-slate-100 text-slate-800 border-slate-200', label: 'Betrachter' },
    // Legacy roles
    admin: { icon: Shield, color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Admin' },
    user: { icon: Users, color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Benutzer' }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData] = await Promise.all([
        authService.getUsers(),
        rbacService.getRoles().catch(() => ({ global_roles: [], tenant_roles: [] }))
      ]);
      setUsers(usersData.users || []);
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 403) {
        toast.error('Sie haben keine Berechtigung für die Benutzerverwaltung');
      } else if (err.response?.status === 401) {
        toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
      } else {
        toast.error('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      console.log('Loading users...');
      const data = await authService.getUsers();
      console.log('Users loaded:', data);
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.status === 403) {
        toast.error('Sie haben keine Berechtigung für die Benutzerverwaltung');
      } else if (err.response?.status === 401) {
        toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
      } else {
        toast.error('Fehler beim Laden der Benutzer');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setActionLoading(true);
    try {
      const userData = {
        email: newUser.email,
        full_name: newUser.full_name,
        role: newUser.role,
        global_role: newUser.global_role || null
      };
      const result = await authService.createUser(userData);
      setCreatedPassword(result.temp_password);
      toast.success('Benutzer erstellt');
      loadData();
      setNewUser({ email: '', full_name: '', role: 'viewer', global_role: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const result = await authService.resetUserPassword(selectedUser.user_id);
      setCreatedPassword(result.temp_password);
      toast.success('Passwort zurückgesetzt');
    } catch (err) {
      toast.error('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await authService.deleteUser(selectedUser.user_id);
      toast.success('Benutzer gelöscht');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen des Benutzers');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await authService.updateUser(user.user_id, { active: !user.active });
      toast.success(user.active ? 'Benutzer deaktiviert' : 'Benutzer aktiviert');
      loadData();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren des Benutzers');
    }
  };

  const handleRoleChange = async (user, newRole) => {
    try {
      await authService.updateUser(user.user_id, { role: newRole });
      toast.success('Mandanten-Rolle aktualisiert');
      loadData();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren der Rolle');
    }
  };

  const handleGlobalRoleChange = async (user, newGlobalRole) => {
    try {
      await authService.updateUser(user.user_id, { global_role: newGlobalRole || null });
      toast.success('Globale Rolle aktualisiert');
      loadData();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren der globalen Rolle');
    }
  };

  const getRoleBadge = (role, globalRole) => {
    // Prioritize global role display
    const displayRole = globalRole || role;
    const config = ROLE_CONFIG[displayRole] || ROLE_CONFIG.user;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
        {globalRole && <span className="text-xs opacity-70">(Global)</span>}
      </Badge>
    );
  };

  const getAuthProviderBadge = (provider) => {
    if (provider === 'entra') {
      return <Badge className="bg-cyan-100 text-cyan-800 border border-cyan-200">Microsoft SSO</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Lokal</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Benutzerverwaltung
              </CardTitle>
              <CardDescription>Benutzer erstellen, bearbeiten und verwalten</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Aktualisieren
              </Button>
              <Button 
                size="sm" 
                onClick={() => setShowCreateDialog(true)}
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)' }}
                data-testid="create-user-button"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Neuer Benutzer
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 border-b border-slate-200">
                  <TableHead className="text-slate-700 font-semibold">Benutzer</TableHead>
                  <TableHead className="text-slate-700 font-semibold">Globale Rolle</TableHead>
                  <TableHead className="text-slate-700 font-semibold">Mandanten-Rolle</TableHead>
                  <TableHead className="text-slate-700 font-semibold">Auth-Methode</TableHead>
                  <TableHead className="text-slate-700 font-semibold">Status</TableHead>
                  <TableHead className="text-slate-700 font-semibold">Erstellt</TableHead>
                  <TableHead className="text-slate-700 font-semibold text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{user.full_name || user.email}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={user.global_role || 'none'}
                        onValueChange={(value) => handleGlobalRoleChange(user, value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="w-36 h-8">
                          <SelectValue placeholder="Keine" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="none">Keine</SelectItem>
                          {roles.global_roles?.map(role => (
                            <SelectItem key={role.role_id} value={role.name}>
                              {role.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={user.role || 'viewer'}
                        onValueChange={(value) => handleRoleChange(user, value)}
                      >
                        <SelectTrigger className="w-36 h-8">
                          <SelectValue>
                            {/* Show current role label */}
                            {roles.tenant_roles?.find(r => r.name === user.role)?.display_name || 
                             ROLE_CONFIG[user.role]?.label || 
                             user.role || 'Betrachter'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {roles.tenant_roles?.length > 0 ? (
                            roles.tenant_roles.map(role => (
                              <SelectItem key={role.role_id} value={role.name}>
                                {role.display_name}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="viewer">Betrachter</SelectItem>
                              <SelectItem value="supporter">Support</SelectItem>
                              <SelectItem value="accountant">Buchhaltung</SelectItem>
                              <SelectItem value="tenant_admin">Kunden-Admin</SelectItem>
                              <SelectItem value="user">Benutzer (Legacy)</SelectItem>
                              <SelectItem value="admin">Admin (Legacy)</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{getAuthProviderBadge(user.auth_provider)}</TableCell>
                    <TableCell>
                      <Switch
                        checked={user.active !== false}
                        onCheckedChange={() => handleToggleActive(user)}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('de-DE') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setCreatedPassword('');
                            setShowResetDialog(true);
                          }}
                          disabled={user.auth_provider === 'entra' && !user.hashed_password}
                          title="Passwort zurücksetzen"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteDialog(true);
                          }}
                          title="Benutzer löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {users.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p>Keine Benutzer vorhanden</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
            <DialogDescription>
              Ein temporäres Passwort wird automatisch generiert.
            </DialogDescription>
          </DialogHeader>
          
          {createdPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Benutzer erstellt!</span>
                </div>
                <p className="text-sm text-green-700 mb-2">Temporäres Passwort:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white rounded border font-mono text-lg">
                    {createdPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdPassword);
                      toast.success('Passwort kopiert');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  Bitte teilen Sie dieses Passwort sicher mit dem Benutzer.
                </p>
              </div>
              <Button className="w-full" onClick={() => { setShowCreateDialog(false); setCreatedPassword(''); }}>
                Schließen
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="benutzer@firma.de"
                  className="mt-1"
                  style={{ backgroundColor: 'white' }}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="Max Mustermann"
                  className="mt-1"
                  style={{ backgroundColor: 'white' }}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label>Globale Rolle (Betreiber)</Label>
                <Select
                  value={newUser.global_role || 'none'}
                  onValueChange={(value) => setNewUser({ ...newUser, global_role: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                    <SelectValue placeholder="Keine (Standard-Benutzer)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Keine</SelectItem>
                    {roles.global_roles?.map(role => (
                      <SelectItem key={role.role_id} value={role.name}>
                        {role.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Nur für Betreiber-Mitarbeiter (systemweit)
                </p>
              </div>
              <div>
                <Label>Mandanten-Rolle</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {roles.tenant_roles?.map(role => (
                      <SelectItem key={role.role_id} value={role.name}>
                        {role.display_name}
                      </SelectItem>
                    ))}
                    {(!roles.tenant_roles || roles.tenant_roles.length === 0) && (
                      <>
                        <SelectItem value="viewer">Betrachter</SelectItem>
                        <SelectItem value="supporter">Support</SelectItem>
                        <SelectItem value="accountant">Buchhaltung</SelectItem>
                        <SelectItem value="tenant_admin">Mandant-Admin</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Rolle innerhalb des Mandanten
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleCreateUser} 
                  disabled={actionLoading || !newUser.email}
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)' }}
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Erstellen'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Passwort zurücksetzen</DialogTitle>
            <DialogDescription>
              Für: {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {createdPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Passwort zurückgesetzt!</span>
                </div>
                <p className="text-sm text-green-700 mb-2">Neues temporäres Passwort:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white rounded border font-mono text-lg">
                    {createdPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdPassword);
                      toast.success('Passwort kopiert');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button className="w-full" onClick={() => { setShowResetDialog(false); setCreatedPassword(''); }}>
                Schließen
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-700">
                    Ein neues temporäres Passwort wird generiert. Der Benutzer muss es bei der nächsten Anmeldung ändern.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleResetPassword} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Zurücksetzen'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Benutzer löschen</DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">{selectedUser?.email}</p>
                <p className="text-sm text-red-700 mt-1">
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser} 
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
